// The words around blog comments: the section under an article, the comments
// page, and every answer the form can give.
//
// The comments themselves are not copy. A reader writes one in whatever
// language they read the article in, and it is shown as they wrote it.

import { fromLocale } from "../load";
import type { TranslatedLocale } from "../locales";

export interface CommentsCopy {
  /** The heading of the section under an article, and of the comments page's list. */
  readonly heading: string;
  /** One line under that heading: how a comment comes to be shown. */
  readonly note: string;
  /** The link from an article to its comments page. */
  readonly readLink: string;
  /** The heading over the form. */
  readonly leaveComment: string;
  /** The label of the name field. */
  readonly nameLabel: string;
  /** The label of the text field. */
  readonly textLabel: string;
  /** The hint under the text field. */
  readonly textHint: string;
  /** The label of the hidden field only a form-filling script fills. */
  readonly honeypotLabel: string;
  /** The form's button. */
  readonly submit: string;
  /** The comments page's document title; `{title}` is the article's title. */
  readonly pageTitle: string;
  /** The comments page's meta description; `{title}` is the article's title. */
  readonly pageDescription: string;
  /** The link from the comments page back to its article. */
  readonly backToArticle: string;
  /** Shown when an article has no published comment. */
  readonly empty: string;
  /** Shown when the comments could not be read. */
  readonly unavailable: string;
  /** Printed before the author's name over a reply. */
  readonly replyFrom: string;
  /** Shown after a comment was accepted. */
  readonly thanks: string;
  readonly nameMissing: string;
  readonly nameTooLong: string;
  readonly textTooShort: string;
  readonly textTooLong: string;
  /** Shown when one address sent too many comments in the hour. */
  readonly rateLimited: string;
  /** Shown when the comment could not be handed over at all. */
  readonly failed: string;
}

export const COMMENTS_EN: CommentsCopy = {
  heading: "Comments",
  note: "A person reads every comment before it appears. Criticism is published together with an answer from the author.",
  readLink: "Read the comments",
  leaveComment: "Leave a comment",
  nameLabel: "Your name",
  textLabel: "Your comment",
  textHint: "Plain text, up to 2,000 characters. Links are not shown.",
  honeypotLabel: "Leave this field empty",
  submit: "Send comment",
  pageTitle: "Comments – {title}",
  pageDescription: "Readers' comments on “{title}”, each read by a person before it appears.",
  backToArticle: "Back to the article",
  empty: "No comments yet. Be the first to answer the question at the end of the article.",
  unavailable: "The comments cannot be shown right now. Please try again later.",
  replyFrom: "Reply from",
  thanks: "Thanks, your comment will appear after review.",
  nameMissing: "Please enter your name.",
  nameTooLong: "The name must be at most 60 characters.",
  textTooShort: "The comment must be at least 3 characters.",
  textTooLong: "The comment must be at most 2,000 characters.",
  rateLimited: "Too many comments from your address. Please try again in an hour.",
  failed: "The comment could not be sent. Please try again later.",
};

const modules = import.meta.glob("../locales/*/comments.ts");

export function loadCommentsCopy(locale: TranslatedLocale): Promise<CommentsCopy> {
  return fromLocale<CommentsCopy>(modules, locale, "comments");
}
